import React, { useMemo } from 'react';

import { Empty } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Spinner } from 'components/UI';
import { useProductIdParam } from 'screens/ProductDetails/common/hooks';
import { useProductSalesForecastQuery } from 'services/graphql/main';
import { useError } from 'services/utils';

import { FORECAST_COLORS, PROJECTED_PATTERN_ID } from './constants';
import ForecastLegend from './ForecastLegend';
import ForecastTooltip from './ForecastTooltip';
import { buildForecastPoints, ForecastPoint, hasAnyForecast } from './helpers';
import { ChartPane, ChartWrapper, PanelGap, Split, TablePane, TableWrapper } from './styles';

/** Width reserved for the Y axis. Identical on both panes, otherwise they drift apart. */
const AXIS_WIDTH = 56;

/** Hatch pattern. Duplicated for the legend, which lives outside the chart SVG. */
const HatchPattern: React.FC<{ id: string }> = ({ id }) => (
  <pattern id={id} width={6} height={6} patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
    <rect width={6} height={6} fill={FORECAST_COLORS.projectedStock} />
    <line x1={0} y1={0} x2={0} y2={6} stroke={FORECAST_COLORS.surface} strokeWidth={2} />
  </pattern>
);

const SalesForecastContent: React.FC = () => {
  const { t } = useTranslation('translation');
  const { addError } = useError();
  const { productId } = useProductIdParam();

  const { loading, data } = useProductSalesForecastQuery({
    variables: { productId },
    onError: (err) => addError(err, 'error'),
  });

  const points: ForecastPoint[] = useMemo(
    () => (data?.productSalesForecast ? buildForecastPoints(data.productSalesForecast) : []),
    [data],
  );

  const currentPoint = points.find((p) => p.isCurrent);
  const hasForecast = data?.productSalesForecast ? hasAnyForecast(data.productSalesForecast) : false;

  if (loading) {
    return <Spinner />;
  }

  if (!points.length || !hasForecast) {
    return <Empty description={t('productOptimalStock.sales_forecast.no_data')} />;
  }

  const lastDonePoint = currentPoint ? points[points.indexOf(currentPoint) - 1] : undefined;

  /*
   * Repeated on each pane so the eye reads them as a single chart.
   */
  const band = lastDonePoint && (
    <ReferenceArea x1={points[0].label} x2={lastDonePoint.label} fill={FORECAST_COLORS.pastBand} fillOpacity={1} />
  );

  const today = (withLabel: boolean) =>
    currentPoint && (
      <ReferenceLine
        x={currentPoint.label}
        stroke={FORECAST_COLORS.today}
        strokeWidth={2}
        label={
          withLabel
            ? {
                value: t('productOptimalStock.sales_forecast.today'),
                position: 'top',
                fill: FORECAST_COLORS.sales,
                fontSize: 12,
              }
            : undefined
        }
      />
    );

  const rowClass = (p: ForecastPoint) => {
    if (p.isCurrent) return 'is-current';

    return p.projectedStock !== null ? 'is-future' : undefined;
  };

  return (
    <Split>
      <TablePane>
        <TableWrapper>
          <table>
            <caption>{t('productOptimalStock.sales_forecast.title')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('productOptimalStock.sales_forecast.week')}</th>
                <th scope="col">{t('productOptimalStock.sales_forecast.sales')}</th>
                <th scope="col">{t('productOptimalStock.sales_forecast.store_stock')}</th>
                <th scope="col">{t('productOptimalStock.sales_forecast.warehouse_stock')}</th>
                <th scope="col">{t('productOptimalStock.sales_forecast.outstanding_supply')}</th>
                <th scope="col">{t('productOptimalStock.sales_forecast.projected_stock')}</th>
              </tr>
            </thead>
            <tbody>
              {points.map((p) => (
                <tr key={`${p.year}-${p.week}`} className={rowClass(p)}>
                  <th scope="row">{p.label}</th>
                  <td>{p.realizedSales ?? p.forecastSales ?? '—'}</td>
                  <td>{p.storeStock ?? '—'}</td>
                  <td>{p.warehouseStock ?? '—'}</td>
                  <td>{p.outstandingSupply ?? '—'}</td>
                  <td>{p.projectedStock ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrapper>
      </TablePane>

      <ChartPane>
        <ChartWrapper>
          {/* Deux panneaux : sur un axe commun la courbe serait ecrasee, et un second axe
              ferait mentir les proportions. */}
          <ResponsiveContainer width="100%" height={230}>
            <ComposedChart data={points} margin={{ top: 28, right: 16, bottom: 0, left: 0 }}>
              <defs>
                <HatchPattern id={PROJECTED_PATTERN_ID} />
              </defs>
              {band}
              <CartesianGrid vertical={false} stroke="#e8e8e8" />
              <XAxis dataKey="label" tick={false} axisLine={{ stroke: '#d9d9d9' }} height={1} />
              <YAxis tickLine={false} axisLine={false} width={AXIS_WIDTH} />
              <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} content={<ForecastTooltip />} />

              <Bar
                dataKey="storeStock"
                stackId="stock"
                fill={FORECAST_COLORS.storeStock}
                stroke={FORECAST_COLORS.surface}
                strokeWidth={2}
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="warehouseStock"
                stackId="stock"
                fill={FORECAST_COLORS.warehouseStock}
                stroke={FORECAST_COLORS.surface}
                strokeWidth={2}
                radius={[4, 4, 0, 0]}
              />
              {/* Same stack as the observed stock: they never coexist, and three slots would
                  thin every bar for nothing. */}
              <Bar
                dataKey="projectedStock"
                stackId="stock"
                fill={`url(#${PROJECTED_PATTERN_ID})`}
                stroke={FORECAST_COLORS.surface}
                strokeWidth={2}
                radius={[4, 4, 0, 0]}
              />
              {/* Beside, never stacked: stacked, it would claim to be part of the stock. */}
              <Bar
                dataKey="outstandingSupply"
                fill={FORECAST_COLORS.outstandingSupply}
                stroke={FORECAST_COLORS.surface}
                strokeWidth={2}
                radius={[4, 4, 0, 0]}
              />
              {today(true)}
            </ComposedChart>
          </ResponsiveContainer>

          <PanelGap />

          <ResponsiveContainer width="100%" height={150}>
            <ComposedChart data={points} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
              {band}
              <CartesianGrid vertical={false} stroke="#e8e8e8" />
              <XAxis dataKey="label" tickLine={false} axisLine={{ stroke: '#d9d9d9' }} />
              <YAxis tickLine={false} axisLine={false} width={AXIS_WIDTH} />
              <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} content={<ForecastTooltip />} />

              {/* Empty bar: its mere presence switches the axis to a band scale, without
                  which the two panes drift by half a band. */}
              <Bar dataKey="axisAlignment" fill="none" isAnimationActive={false} />

              <Line
                type="monotone"
                dataKey="realizedSales"
                stroke={FORECAST_COLORS.sales}
                strokeWidth={2}
                dot={{ r: 4, strokeWidth: 2, fill: FORECAST_COLORS.surface }}
                activeDot={{ r: 6 }}
                connectNulls={false}
              />
              <Line
                type="monotone"
                dataKey="forecastSales"
                stroke={FORECAST_COLORS.sales}
                strokeWidth={2}
                strokeDasharray="6 4"
                dot={{ r: 4, strokeWidth: 2, fill: FORECAST_COLORS.surface, strokeDasharray: '0' }}
                activeDot={{ r: 6 }}
                connectNulls={false}
              />
              {today(false)}
            </ComposedChart>
          </ResponsiveContainer>

          {/* The legend serves both panes, hence the pattern duplicated outside the SVG. */}
          <svg width="0" height="0" aria-hidden="true">
            <defs>
              <HatchPattern id={`${PROJECTED_PATTERN_ID}-legend`} />
            </defs>
          </svg>
          <ForecastLegend />
        </ChartWrapper>
      </ChartPane>
    </Split>
  );
};

export default SalesForecastContent;
